import { animate, state, style, transition, trigger } from '@angular/animations';
import { DatePipe, formatDate } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ChartType } from 'angular-google-charts';
import { Subscription } from 'rxjs';
import { API_BASE_URL_SERVICE } from '../../../../../projects/kiene-core/src/lib/kiene-core.config';
import { MessageService } from '../../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { CompanyGroup } from '../../../administration/company-group/company-group';
import { KieneTableService } from '../../../components/kiene-table/kiene-table.service';
import { CsvColumn } from '../../../csv-export/csv-export/csv-column';
import { FileDownloadService } from '../../../services/file-download.service';
import { ReportUtils } from '../../report-utils';
import { ReportService } from '../../report.service';
import { ZeitauswahlChange } from '../../zeitauswahl/zeitauswahl-change';
import { KundeArtikelReportDialogComponent } from './kunde-artikel-report-dialog/kunde-artikel-report-dialog.component';
import { UnternehmensgruppeEinzel } from './unternehmensgruppe-einzel';

export class KundeArtikelReportInput {
    kunde_id: number;
    von: Date;
    bis: Date;
}

@Component({
    selector: 'kiene-unternehmensgruppe-einzel-report',
    templateUrl: './unternehmensgruppe-einzel-report.component.html',
    styleUrls: ['./unternehmensgruppe-einzel-report.component.scss'],
    animations: [
        trigger('detailExpanded', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
})
export class UnternehmensgruppeEinzelReportComponent implements OnInit, AfterViewInit {
    displayedColumns = [
        'kunde',
        'umsatz_prozent',
        'umsatz',
        'umsatz_delta_prozent',
        'wareneinsatz',
        'wareneinsatz_prozent',
        'rohertrag',
        'rohertrag_prozent',
        'abgabebelege',
        'rueckverguetung',
        'artikelverbrauch',
    ];
    dataSource = new MatTableDataSource<UnternehmensgruppeEinzel>();
    dataSourceVorjahr = new MatTableDataSource<UnternehmensgruppeEinzel>();
    @ViewChild(MatSort) sort: MatSort;
    expandedElement: UnternehmensgruppeEinzel | null;

    currentUnternehmensgruppe: CompanyGroup;

    abgabebelegeGesamt = 0;
    abgabebelegeGesamtVorjahr = 0;

    rabattCtrl = new FormControl();
    ergaenzerCtrl = new FormControl();
    umsatzFilterCtrl = new FormControl('alle');
    anzahlAktuell = 0;
    private aktuellRecords: UnternehmensgruppeEinzel[] = [];
    private vorjahrRecords: UnternehmensgruppeEinzel[] = [];
    aktuellSubscription: Subscription;
    vorjahrSubscription: Subscription;

    zeitauswahl: ZeitauswahlChange;
    vorjahreszeitraum: ZeitauswahlChange;

    chartType = ChartType.Line;
    chartColumns = ['Zeitraum', 'Umsatz', 'Wareneinsatz', 'Rohertrag'];
    chartData = [[new Date(), 0, 0, 0]];

    csvColumns: CsvColumn[] = [];
    csvColumnsDetails: CsvColumn[] = [];
    csvFilename = 'Unternehmensgruppe_Einzel.csv';

    gesamtUmsatz = 0;
    gesamtWareneinsatz = 0;
    gesamtWareneinsatzProzent = '0';
    gesamtRohertragProzent = '0';
    gesamtRohertrag = 0;

    gesamtUmsatzVorjahr = 0;
    gesamtWareneinsatzVorjahr = 0;
    gesamtWareneinsatzProzentVorjahr = '0';
    gesamtRohertragProzentVorjahr = '0';
    gesamtRohertragVorjahr = 0;

    isLoading: boolean[] = [];

    constructor(
        private readonly api: KieneTableService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
        private readonly datePipe: DatePipe,
        private readonly reportService: ReportService,
        private readonly messageService: MessageService,
        private readonly dialog: MatDialog,
        private readonly fileDownloadService: FileDownloadService,
    ) {}

    ngOnInit(): void {
        this.csvColumns.push(new CsvColumn('kunde', 'Kunde'));
        this.csvColumns.push(new CsvColumn('umsatz_prozent', 'Umsatz von Gesamt (%)', '%'));
        this.csvColumns.push(new CsvColumn('umsatz', 'Umsatz'));
        this.csvColumns.push(new CsvColumn('umsatz_vorjahr', 'Umsatz Vorjahr'));
        this.csvColumns.push(new CsvColumn('umsatz_delta_prozent', 'Umsatz ggü. Vorjahr (%)', '%'));
        this.csvColumns.push(new CsvColumn('wareneinsatz', 'Wareneinsatz'));
        this.csvColumns.push(new CsvColumn('wareneinsatz_prozent', 'Wareneinsatz (%)', '%'));
        this.csvColumns.push(new CsvColumn('rohertrag', 'Rohertrag'));
        this.csvColumns.push(new CsvColumn('rohertrag_prozent', 'Rohertrag (%)', '%'));
        this.csvColumns.push(new CsvColumn('abgabebelege', 'Anzahl Abgabebelege'));

        this.csvColumnsDetails = this.csvColumns
            .filter(column => column.name !== 'umsatz_vorjahr' && column.name !== 'umsatz_delta_prozent')
            .map(column => ({ ...column }));
        this.csvColumnsDetails.splice(0, 1, new CsvColumn('zeitraum', 'Zeitraum'));

        this.dataSource.filterPredicate = (row, filter) => ReportUtils.passtUmsatzVorjahrFilter(row, filter);
        this.umsatzFilterCtrl.valueChanges.subscribe(value => {
            this.dataSource.filter = value || 'alle';
        });
        this.dataSource.filter = 'alle';
    }

    ngAfterViewInit() {
        this.dataSource.sort = this.sort;
        this.dataSource.sortingDataAccessor = (row, column) => {
            if (column === 'umsatz_delta_prozent') {
                return row.umsatz_delta_prozent == null ? Number.POSITIVE_INFINITY : row.umsatz_delta_prozent;
            }
            const value = (row as unknown as Record<string, unknown>)[column];
            return typeof value === 'string' ? value.toLowerCase() : (value as string | number);
        };
    }

    loadData() {
        this.loadDataAktuell();
        this.loadDataVorjahr();
    }

    loadDataAktuell() {
        // if(!this.currentUnternehmensgruppe){
        //   return;
        // }
        let params = this.createUnternehmensgruppeParams();

        params = params.set('von', this.datePipe.transform(this.zeitauswahl.von, 'yyyy-MM-dd'));
        params = params.set('bis', this.datePipe.transform(this.zeitauswahl.bis, 'yyyy-MM-dd'));

        this.aktuellSubscription?.unsubscribe();
        this.aktuellSubscription = this.api.get(this.apiBaseUrl + 'report/read_kunde_ug.php', params).subscribe(
            response => {
                this.aktuellRecords = response.records ?? [];
                this.anzahlAktuell = this.aktuellRecords.length;
                this.resetChartData();
                this.berechneGesamtwerte();
                this.applyUmsatzVergleich();
            },
            error => {
                this.messageService.errorMessage('Fehler!', error);
            },
        );
    }

    loadDataVorjahr() {
        let params = this.createUnternehmensgruppeParams();

        params = params.set('von', this.datePipe.transform(this.vorjahreszeitraum.von, 'yyyy-MM-dd'));
        params = params.set('bis', this.datePipe.transform(this.vorjahreszeitraum.bis, 'yyyy-MM-dd'));

        this.vorjahrSubscription?.unsubscribe();
        this.vorjahrSubscription = this.api.get(this.apiBaseUrl + 'report/read_kunde_ug.php', params).subscribe(
            response => {
                this.vorjahrRecords = response.records ?? [];
                this.dataSourceVorjahr.data = this.vorjahrRecords;
                this.berechneGesamtwerteVorjahr();
                this.applyUmsatzVergleich();
            },
            error => {
                this.messageService.errorMessage('Fehler!', error);
            },
        );
    }

    createUnternehmensgruppeParams(): HttpParams {
        let params = new HttpParams();
        if (this.currentUnternehmensgruppe) {
            params = params.set(
                'unternehmensgruppe_id',
                this.currentUnternehmensgruppe.unternehmensgruppe_id.toString(),
            );
        } else {
            params = params.set('unternehmensgruppe_id', 'null');
        }
        params = params.set('rabatt', this.rabattCtrl.value ? '1' : '0');
        params = params.set('ergaenzer', this.ergaenzerCtrl.value ? '1' : '0');
        return params;
    }

    setCurrentUnternehmensgruppe(ug: CompanyGroup) {
        this.currentUnternehmensgruppe = ug;
        this.loadData();
    }

    zeitintervalChanged(event: ZeitauswahlChange) {
        this.zeitauswahl = event;
        this.vorjahreszeitraum = ReportUtils.getVorjahreszeitraum(this.zeitauswahl);
        this.loadData();
    }

    berechneGesamtwerte() {
        this.gesamtUmsatz = 0;
        this.gesamtWareneinsatz = 0;
        this.gesamtRohertrag = 0;
        this.abgabebelegeGesamt = 0;

        const data = this.aktuellRecords;
        for (const r of data) {
            this.abgabebelegeGesamt += r.abgabebelege ? r.abgabebelege : 0;
            this.gesamtUmsatz += r.umsatz ? r.umsatz : 0;
            this.gesamtWareneinsatz += r.wareneinsatz ? r.wareneinsatz : 0;
            this.gesamtRohertrag += r.rohertrag ? r.rohertrag : 0;
        }
        if (this.gesamtUmsatz > 0) {
            this.gesamtWareneinsatzProzent = (
                Math.round((100 / this.gesamtUmsatz) * this.gesamtWareneinsatz * 100) / 100
            ).toString();
            this.gesamtRohertragProzent = (
                Math.round((100 / this.gesamtUmsatz) * this.gesamtRohertrag * 100) / 100
            ).toString();
        }
    }

    berechneGesamtwerteVorjahr() {
        this.gesamtUmsatzVorjahr = 0;
        this.gesamtWareneinsatzVorjahr = 0;
        this.gesamtRohertragVorjahr = 0;
        this.abgabebelegeGesamtVorjahr = 0;

        const data = this.vorjahrRecords;
        for (const r of data) {
            this.abgabebelegeGesamtVorjahr += r.abgabebelege ? r.abgabebelege : 0;
            this.gesamtUmsatzVorjahr += r.umsatz ? r.umsatz : 0;
            this.gesamtWareneinsatzVorjahr += r.wareneinsatz ? r.wareneinsatz : 0;
            this.gesamtRohertragVorjahr += r.rohertrag ? r.rohertrag : 0;
        }
        if (this.gesamtUmsatzVorjahr > 0) {
            this.gesamtWareneinsatzProzentVorjahr = (
                Math.round((100 / this.gesamtUmsatzVorjahr) * this.gesamtWareneinsatzVorjahr * 100) / 100
            ).toString();
            this.gesamtRohertragProzentVorjahr = (
                Math.round((100 / this.gesamtUmsatzVorjahr) * this.gesamtRohertragVorjahr * 100) / 100
            ).toString();
        }
    }

    private applyUmsatzVergleich(): void {
        this.dataSource.data = ReportUtils.mergeUmsatzVorjahr(
            this.aktuellRecords,
            this.vorjahrRecords,
            row => row.kunde_id,
            row => ReportUtils.ohneAktuellenUmsatz(row),
        );
    }

    expandVetGroupRow(element: UnternehmensgruppeEinzel) {
        this.expandedElement = this.expandedElement === element ? null : element;
        if (this.expandedElement) {
            this.isLoading[element.kunde_id] = true;

            let params = new HttpParams();
            params = params.set('von', formatDate(this.zeitauswahl?.von, 'yyyy-MM-dd', 'de'));
            params = params.set('bis', formatDate(this.zeitauswahl?.bis, 'yyyy-MM-dd', 'de'));
            params = params.set('aggregieren', this.zeitauswahl?.aggregieren);
            params = params.set('kunde_id', element.kunde_id.toString());
            this.api.get(this.apiBaseUrl + 'report/read_kunde_ug.php', params).subscribe(
                response => {
                    element.details = response.records;
                    this.isLoading[element.kunde_id] = false;
                    this.chartData = [];
                    for (const d of element.details) {
                        this.chartData.push([
                            d.datum,
                            d.umsatz ? d.umsatz : 0,
                            d.wareneinsatz ? d.wareneinsatz : 0,
                            d.rohertrag ? d.rohertrag : 0,
                        ]);
                    }
                },
                error => {
                    this.messageService.errorMessage('Fehler!', error);
                    this.isLoading[element.kunde_id] = false;
                },
            );
        }
    }

    resetChartData() {
        this.chartData = [[new Date(), 0, 0, 0]];
    }

    rueckverguetung(kunde_id: number) {
        this.reportService.rueckverguetung(kunde_id, this.zeitauswahl.von, this.zeitauswahl.bis);
    }

    openArtikelDialog(kunde_id: any) {
        const input = new KundeArtikelReportInput();
        input.kunde_id = kunde_id;
        input.von = this.zeitauswahl.von;
        input.bis = this.zeitauswahl.bis;
        this.dialog.open(KundeArtikelReportDialogComponent, { data: input, width: '90vw' });
    }

    downloadArtikelExcel() {
        const params = this.createArtikelDownloadParams();
        if (!params) {
            return;
        }
        this.fileDownloadService.downloadExcel('report/read_kunde_ug_artikel.php', params);
    }

    downloadArtikelCsv() {
        const params = this.createArtikelDownloadParams();
        if (!params) {
            return;
        }
        this.fileDownloadService.downloadCSV('report/read_kunde_ug_artikel.php', params);
    }

    private createArtikelDownloadParams(): HttpParams {
        if (!this.zeitauswahl?.von || !this.zeitauswahl?.bis) {
            this.messageService.alertMessage('Bitte zuerst einen Zeitraum wählen!');
            return null;
        }
        return this.createUnternehmensgruppeParams()
            .set('von', this.datePipe.transform(this.zeitauswahl.von, 'yyyy-MM-dd'))
            .set('bis', this.datePipe.transform(this.zeitauswahl.bis, 'yyyy-MM-dd'));
    }
}
